// src/layouts/menuStructure.ts
//
// The shell's two navigation levels (hybrid-3c.md §4 and §5.1): the rail
// shows one entry per destination, and the pages of the active destination
// are reached through the section bar under the app bar. This module is the
// single description of that structure; the rail (CustomMenu), the section
// bar (SectionTabs), the breadcrumb (CustomAppBar) and the layout inset
// (CustomLayout, via useSectionTabs) all read it.
//
// Visibility is decided per page, never per destination: a page is a tab
// only when the current role may access it (resourceConfig) and, for the
// technical pages, when the user holds the technical capability (the error
// monitor and logs also open to chain managers), for the Insight v2 pages,
// when the user has at least one v2 property, and, for «Dispensadores», when
// the user's hotels have a key dispenser. A destination with no visible tab
// does not appear in the rail.
import React from 'react';
import {
  RoomServiceRounded as ReceptionIcon,
  InsightsRounded as DirectionIcon,
  AccountBalanceWalletRounded as FinanceIcon,
  ReportGmailerrorredRounded as IncidentsIcon,
  DnsRounded as SystemsIcon,
  AdminPanelSettingsRounded as AdministrationIcon,
  MenuBookRounded as ManualIcon,
  IntegrationInstructionsRounded as DevelopersIcon,
} from '@mui/icons-material';
import { getMenuItems } from '@/config/resourceConfig';
import { canViewErrorMonitor, getCurrentRole, hasTechnicalCapability } from '@/utils/auth';
import { isV2ScreenOffered } from '@/lib/v2Properties';
import { canSeeDevelopers, DEVELOPERS_ROUTE, isDocumentationOnly } from '@/features/developers/access';

/** Rail blocks: a hairline separates the day-to-day business destinations
 *  from the platform ones (hybrid-3c.md §4 "Group separator"). */
export type NavBlock = 'business' | 'platform';

export interface NavGroup {
  key: string;
  labelKey: string;
  icon: React.ComponentType;
  block: NavBlock;
  /** Resource names shown as tabs, in this order. */
  members: string[];
  /** Routes that belong to the destination without being one of its tabs
   *  (pages reached from inside it), so the rail keeps it active there. */
  relatedRoutes?: string[];
}

export const NAV_GROUPS: NavGroup[] = [
  { key: 'reception', labelKey: 'shell.menu.groups.reception', icon: ReceptionIcon, block: 'business', members: ['arrivals', 'reservations', 'live-board'] },
  { key: 'direction', labelKey: 'shell.menu.groups.direction', icon: DirectionIcon, block: 'business', members: ['summary', 'operacion', 'satisfaction', 'channels', 'dispensers', 'durations', 'hotels-overview', 'benchmark'] },
  { key: 'finance', labelKey: 'shell.menu.groups.finance', icon: FinanceIcon, block: 'business', members: ['payments', 'reconciliation'] },
  { key: 'incidents', labelKey: 'shell.menu.groups.incidents', icon: IncidentsIcon, block: 'platform', members: ['error-monitor', 'events', 'alerts', 'logs', 'error-definitions'] },
  { key: 'systems', labelKey: 'shell.menu.groups.systems', icon: SystemsIcon, block: 'platform', members: ['devices', 'control', 'status', 'simulators'] },
  { key: 'administration', labelKey: 'shell.menu.groups.administration', icon: AdministrationIcon, block: 'platform', members: ['admin-options', 'users', 'fit'], relatedRoutes: ['/users', '/hotels', '/groups'] },
];

/** Resource names shown only when the current user has the technical capability
 *  (Decision 7: `insight:technical` client role, or Admin/SuperAdmin implicitly).
 *  Dispositivos and Alertas are not here: every customer role resourceConfig
 *  admits sees them, scoped to its hotels. */
const TECHNICAL_ONLY_RESOURCES = new Set(['events', 'error-definitions', 'control', 'status']);

/** Monitor de errores and Logs: technical users and chain managers (GroupAdmin). */
const CHAIN_MANAGER_RESOURCES = new Set(['error-monitor', 'logs']);

/** Insight v2 screens: every one reads a property of `GET /api/v2/ui/properties`,
 *  so they are offered only to a user who has at least one. */
export const V2_RESOURCES = new Set(['operacion', 'satisfaction', 'live-board', 'reconciliation', 'control', 'status', 'benchmark', 'fit']);

/** Screens about the key dispensers, offered only when the user's hotels have one (useHasDispensers). */
const DISPENSER_RESOURCES = new Set(['dispensers']);

/** «Ayuda»: the user manual and «Novedades», offered to every signed-in role (the API cuts
 *  each role's edition). Not resources: their routes are `/manual`, `/manual/:chapterId` and
 *  `/novedades`, so the group is appended here, always last. */
const HELP_GROUP = {
  key: 'help',
  labelKey: 'shell.menu.groups.help',
  icon: ManualIcon,
  block: 'platform' as NavBlock,
  tabs: [
    { name: 'manual', to: '/manual', labelKey: 'manual.name' },
    { name: 'release-notes', to: '/novedades', labelKey: 'releaseNotes.name' },
  ] as NavTab[],
};

/** «Desarrolladores»: documentation of the public API. Every role but Receptionist, and the
 *  documentation-only people (for whom it is the whole rail). Its pages hang from `/developers`. */
const DEVELOPERS_GROUP = {
  key: 'developers',
  labelKey: 'developers.name',
  icon: DevelopersIcon,
  block: 'platform' as NavBlock,
  tabs: [{ name: 'developers', to: DEVELOPERS_ROUTE, labelKey: 'developers.name' }] as NavTab[],
};

export interface NavTab {
  name: string;
  to: string;
  labelKey: string;
}

export interface VisibleNavGroup {
  key: string;
  labelKey: string;
  icon: React.ReactNode;
  block: NavBlock;
  tabs: NavTab[];
  relatedRoutes: string[];
}

const matchesRoute = (pathname: string, route: string): boolean =>
  route === '/' ? pathname === '/' : pathname === route || pathname.startsWith(`${route}/`);

/**
 * The destinations the current user may see, each with its visible tabs.
 * `hasV2Property` is false until the user's v2 property list is known to be
 * non-empty (see useHasV2Property), so the v2 tabs never show on a guess;
 * `hasDispensers` likewise until a dispenser is known to exist in the user's
 * hotels (see useHasDispensers); `hasSurveyProperty` until a property with
 * survey answers is known (see useHasSurveyProperty) — it offers «Satisfacción»
 * alone, the screen whose selector also lists hotels with only survey answers.
 * A menu resource missing from NAV_GROUPS becomes a destination of its own,
 * so a new resource never disappears from the rail.
 */
export const getVisibleNavGroups = (
  hasV2Property: boolean,
  hasDispensers = false,
  hasSurveyProperty = false,
): VisibleNavGroup[] => {
  const developers: VisibleNavGroup = {
    key: DEVELOPERS_GROUP.key,
    labelKey: DEVELOPERS_GROUP.labelKey,
    icon: React.createElement(DEVELOPERS_GROUP.icon),
    block: DEVELOPERS_GROUP.block,
    relatedRoutes: [],
    tabs: DEVELOPERS_GROUP.tabs,
  };
  // A documentation-only person sees this destination and no other.
  if (isDocumentationOnly()) return [developers];

  const canSeeTechnical = hasTechnicalCapability();
  const canSeeErrorMonitor = canViewErrorMonitor();
  const passesCapability = (name: string): boolean => {
    if (TECHNICAL_ONLY_RESOURCES.has(name)) return canSeeTechnical;
    if (CHAIN_MANAGER_RESOURCES.has(name)) return canSeeErrorMonitor;
    return true;
  };
  const items = getMenuItems().filter(
    (item) =>
      passesCapability(item.name) &&
      (!V2_RESOURCES.has(item.name) || isV2ScreenOffered(item.name, hasV2Property, hasSurveyProperty))
  );
  const offered = items.filter((item) => hasDispensers || !DISPENSER_RESOURCES.has(item.name));
  const toTab = (item: (typeof items)[number]): NavTab => ({ name: item.name, to: item.route, labelKey: item.textKey });

  const grouped: VisibleNavGroup[] = NAV_GROUPS.map((group) => ({
    key: group.key,
    labelKey: group.labelKey,
    icon: React.createElement(group.icon),
    block: group.block,
    relatedRoutes: group.relatedRoutes ?? [],
    tabs: group.members
      .map((name) => offered.find((item) => item.name === name))
      .filter((item): item is (typeof items)[number] => Boolean(item))
      .map(toTab),
  }));

  const mapped = new Set(NAV_GROUPS.flatMap((group) => group.members));
  const ungrouped: VisibleNavGroup[] = offered
    .filter((item) => !mapped.has(item.name))
    .map((item) => ({
      key: item.name,
      labelKey: item.textKey,
      icon: item.icon,
      block: 'platform',
      relatedRoutes: [],
      tabs: [toTab(item)],
    }));

  const help: VisibleNavGroup = {
    key: HELP_GROUP.key,
    labelKey: HELP_GROUP.labelKey,
    icon: React.createElement(HELP_GROUP.icon),
    block: HELP_GROUP.block,
    relatedRoutes: [],
    tabs: HELP_GROUP.tabs,
  };

  return [...grouped, ...ungrouped, ...(canSeeDevelopers(getCurrentRole(), false) ? [developers] : []), help].filter((group) => group.tabs.length > 0);
};

export interface NavLocation {
  group: VisibleNavGroup;
  /** The active tab, when the path is one of the destination's pages (or a
   *  page below one, e.g. /reservations/:id); absent on a related route. */
  tab?: NavTab;
}

/** Resolve the destination and tab of a path among the visible destinations. */
export const findNavLocation = (pathname: string, groups: VisibleNavGroup[]): NavLocation | null => {
  for (const group of groups) {
    const tab = group.tabs.find((candidate) => matchesRoute(pathname, candidate.to));
    if (tab) return { group, tab };
  }
  const related = groups.find((group) => group.relatedRoutes.some((route) => matchesRoute(pathname, route)));
  return related ? { group: related } : null;
};

/**
 * Where "/" takes the user: the page chosen in «Mis preferencias» when the
 * user may see it, otherwise the first page of the first visible
 * destination (a Receptionist, who cannot see Resumen, lands on Llegadas de
 * hoy). `null` when the user can see no page at all.
 */
export const resolveStartRoute = (preferredRoute: string, groups: VisibleNavGroup[]): string | null => {
  // The manual and the developer documentation are never a start page.
  const tabs = groups.filter((group) => group.key !== HELP_GROUP.key && group.key !== DEVELOPERS_GROUP.key).flatMap((group) => group.tabs);
  if (tabs.some((tab) => tab.to === preferredRoute)) return preferredRoute;
  return tabs[0]?.to ?? null;
};
